import { format as formatDateFns } from 'date-fns'
import { isolateFormattedValue as isolateValue } from './bidi'
import { intlLocale, type Locale } from './locales'

/**
 * Intl puts a right-to-left mark (and sometimes an Arabic letter mark) after each
 * number of an Arabic date. They are strong right-to-left characters, so inside
 * the left-to-right isolate they still reorder the digits ("10/10/2026" read as
 * "102026/10/"). The isolate does their job, so a date drops them first. Money
 * keeps its own output; this applies to dates and times only.
 */
const DATE_DIRECTION_MARKS = /[\u200e\u200f\u061c]/gu
export const isolateFormattedValue = (value: string, locale: Locale): string =>
  isolateValue(value.replace(DATE_DIRECTION_MARKS, ''), locale)

export interface DateTimePreferences {
  timeZone: string
  hour12: boolean
}

const DEFAULT_HOUR12 = true

function browserTimeZone(): string {
  if (typeof Intl === 'undefined') return 'UTC'

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return timeZone || 'UTC'
}

export function getBrowserTimeZone(): string {
  return browserTimeZone()
}

export function isoDateInTimeZone(value: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en', {
    calendar: 'iso8601',
    day: '2-digit',
    month: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(value)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value
  const year = part('year')
  const month = part('month')
  const day = part('day')
  if (!year || !month || !day) throw new Error('Could not format ISO calendar date')
  return `${year}-${month}-${day}`
}

export function todayIsoDate(timeZone = getDateTimePreferences().timeZone): string {
  return isoDateInTimeZone(new Date(), timeZone || browserTimeZone())
}

const DEFAULT_PREFERENCES: DateTimePreferences = {
  timeZone: browserTimeZone(),
  hour12: DEFAULT_HOUR12,
}

let preferences: DateTimePreferences = { ...DEFAULT_PREFERENCES }

function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format()
    return true
  } catch {
    return false
  }
}

export function getDateTimePreferences(): DateTimePreferences {
  return { ...preferences }
}

export function setDateTimePreferences(next: Partial<DateTimePreferences>): void {
  const timeZone = next.timeZone ?? preferences.timeZone
  preferences = {
    timeZone: isTimeZone(timeZone) ? timeZone : DEFAULT_PREFERENCES.timeZone,
    hour12: next.hour12 ?? preferences.hour12,
  }
}

export function resetDateTimePreferences(): void {
  preferences = { ...DEFAULT_PREFERENCES, timeZone: browserTimeZone() }
}

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>()
const relativeTimeFormatters = new Map<string, Intl.RelativeTimeFormat>()

function relativeTimeFormatter(locale: Locale): Intl.RelativeTimeFormat {
  let formatter = relativeTimeFormatters.get(locale)
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'always' })
    relativeTimeFormatters.set(locale, formatter)
  }
  return formatter
}

export function formatRelativeTime(value: Date, locale: Locale, now = new Date()): string {
  const seconds = (value.getTime() - now.getTime()) / 1000
  const absolute = Math.abs(seconds)
  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'minute'],
    [60 * 60, 'hour'],
    [60 * 60 * 24, 'day'],
    [60 * 60 * 24 * 7, 'week'],
    [60 * 60 * 24 * 30, 'month'],
    [60 * 60 * 24 * 365, 'year'],
  ]
  let divisor = 1
  let unit: Intl.RelativeTimeFormatUnit = 'second'
  for (const [threshold, nextUnit] of units) {
    if (absolute < threshold) break
    divisor = threshold
    unit = nextUnit
  }
  return relativeTimeFormatter(locale).format(Math.round(seconds / divisor), unit)
}

function dateTimeFormatter(
  locale: Locale,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}:${JSON.stringify(options)}`
  let formatter = dateTimeFormatters.get(key)

  if (!formatter) {
    formatter = new Intl.DateTimeFormat(intlLocale(locale), options)
    dateTimeFormatters.set(key, formatter)
  }

  return formatter
}

/**
 * English house style, "25 Sep 2026, 1:12 PM": the plain `en` (US) formatter supplies
 * the parts, and the date is rebuilt day first with a short month. The time keeps the
 * parts Intl gives it, so the hour is not padded and the period stays "PM".
 */
function englishHouseDate(parts: Intl.DateTimeFormatPart[]): string {
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? ''
  const date = `${part('day')} ${part('month')} ${part('year')}`
  const yearIndex = parts.findIndex((item) => item.type === 'year')
  const time = parts
    .slice(yearIndex + 1)
    .map((item) => item.value)
    .join('')
    .replace(/^,\s*/u, '')
    // format() prints a plain space before the period; formatToParts keeps U+202F.
    .replaceAll(String.fromCharCode(0x202f), ' ')

  return time ? `${date}, ${time}` : date
}

/**
 * Arabic house style, "25 سبتمبر 2026، 12:30 ص", the same date `formatDate` prints:
 * Intl's Arabic `dateStyle: 'medium'` is numeric ("25/09/2026"), so the date is spelled
 * out with explicit fields instead.
 */
function arabicHouseOptions(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
  const { dateStyle, timeStyle, ...rest } = options
  return {
    ...rest,
    ...(dateStyle ? { day: 'numeric', month: 'long', year: 'numeric' } : {}),
    ...(timeStyle ? { hour: 'numeric', minute: '2-digit' } : {}),
  }
}

function formatDateValue(value: Date, locale: Locale, options: Intl.DateTimeFormatOptions): string {
  if (locale !== 'en') return dateTimeFormatter(locale, arabicHouseOptions(options)).format(value)

  const formatter = dateTimeFormatter(locale, options)

  return englishHouseDate(formatter.formatToParts(value))
}

export function formatDateTime(value: Date, locale: Locale): string {
  return formatDateTimeInTimeZone(value, locale, getDateTimePreferences().timeZone)
}

export function formatDateTimeInTimeZone(value: Date, locale: Locale, timeZone: string): string {
  const current = getDateTimePreferences()
  return isolateFormattedValue(
    formatDateValue(value, locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone,
      hour12: current.hour12,
    }),
    locale,
  )
}

export function formatDateInTimeZone(value: Date, locale: Locale, timeZone: string): string {
  return isolateFormattedValue(
    formatDateValue(value, locale, { dateStyle: 'medium', timeZone }),
    locale,
  )
}

export function formatClock(value: string, locale: Locale): string {
  const instant = new Date(value)
  if (Number.isNaN(instant.getTime())) return isolateFormattedValue(value, locale)

  return formatClockInTimeZone(instant, locale, getDateTimePreferences().timeZone)
}

export function formatClockInTimeZone(value: Date, locale: Locale, timeZone: string): string {
  const current = getDateTimePreferences()
  return isolateFormattedValue(
    dateTimeFormatter(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: current.hour12,
      timeZone,
    }).format(value),
    locale,
  )
}

/** Format a stored local HH:mm value without shifting its business date. */
export function formatLocalTime(value: string, locale: Locale): string {
  const [hours, minutes] = value.split(':').map(Number)
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return isolateFormattedValue(value, locale)
  }

  const current = getDateTimePreferences()
  return isolateFormattedValue(
    dateTimeFormatter(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: current.hour12,
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(2024, 0, 1, hours, minutes))),
    locale,
  )
}

/** The same instant as the time input's required, unlocalised HH:mm value. */
export function toClockInput(value: string): string {
  const instant = new Date(value)
  if (Number.isNaN(instant.getTime())) return ''

  const current = getDateTimePreferences()
  const parts = dateTimeFormatter('en', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: current.timeZone,
  }).formatToParts(instant)
  const hour = parts.find((part) => part.type === 'hour')?.value
  const minute = parts.find((part) => part.type === 'minute')?.value

  return hour && minute ? `${hour}:${minute}` : formatDateFns(instant, 'HH:mm')
}
